<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';
import GreetingsEditor from 'shared/components/GreetingsEditor.vue';
import { WIDGET_BUILDER_EDITOR_MENU_OPTIONS } from 'dashboard/constants/editor';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

export default {
  components: {
    PageHeader,
    GreetingsEditor,
    NextButton,
    Editor,
  },
  data() {
    return {
      inboxName: '',
      channelWebsiteUrl: '',
      channelWidgetColor: '#009CE0',
      channelWelcomeTitle: '',
      channelWelcomeTagline: '',
      greetingEnabled: false,
      greetingMessage: '',
      welcomeTaglineEditorMenuOptions: WIDGET_BUILDER_EDITOR_MENU_OPTIONS,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    textAreaChannels() {
      if (
        this.isATwilioChannel ||
        this.isATwitterInbox ||
        this.isAFacebookInbox
      )
        return true;
      return false;
    },
  },
  methods: {
    async createChannel() {
      try {
        const website = await this.$store.dispatch(
          'inboxes/createWebsiteChannel',
          {
            name: this.inboxName,
            greeting_enabled: this.greetingEnabled,
            greeting_message: this.greetingMessage,
            channel: {
              type: 'web_widget',
              website_url: this.channelWebsiteUrl,
              widget_color: this.channelWidgetColor,
              welcome_title: this.channelWelcomeTitle,
              welcome_tagline: this.channelWelcomeTagline,
            },
          }
        );
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: website.id,
          },
        });
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="ch-form-page h-full w-full overflow-y-auto">
    <div class="ch-form-page__intro">
      <h2 class="ch-form-page__title">
        {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.TITLE') }}
      </h2>
      <p class="ch-form-page__desc">
        {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.DESC') }}
      </p>
    </div>

    <woot-loading-state
      v-if="uiFlags.isCreating"
      :message="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.LOADING_MESSAGE')"
    />

    <form v-if="!uiFlags.isCreating" @submit.prevent="createChannel">
      <!-- Inbox Name -->
      <div class="ch-form-row">
        <div class="ch-form-row__label-col">
          <label class="ch-form-label">{{
            $t('INBOX_MGMT.ADD.WEBSITE_NAME.LABEL')
          }}</label>
        </div>
        <div class="ch-form-row__control-col">
          <input
            v-model="inboxName"
            type="text"
            class="ch-input"
            :placeholder="$t('INBOX_MGMT.ADD.WEBSITE_NAME.PLACEHOLDER')"
          />
        </div>
      </div>

      <!-- Website URL -->
      <div class="ch-form-row">
        <div class="ch-form-row__label-col">
          <label class="ch-form-label">{{
            $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.LABEL')
          }}</label>
        </div>
        <div class="ch-form-row__control-col">
          <input
            v-model="channelWebsiteUrl"
            type="text"
            class="ch-input"
            :placeholder="
              $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.PLACEHOLDER')
            "
          />
        </div>
      </div>

      <!-- Widget Color -->
      <div class="ch-form-row">
        <div class="ch-form-row__label-col">
          <label class="ch-form-label">{{
            $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.WIDGET_COLOR.LABEL')
          }}</label>
        </div>
        <div class="ch-form-row__control-col">
          <woot-color-picker v-model="channelWidgetColor" />
        </div>
      </div>

      <!-- Welcome Title -->
      <div class="ch-form-row">
        <div class="ch-form-row__label-col">
          <label class="ch-form-label">{{
            $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.LABEL')
          }}</label>
        </div>
        <div class="ch-form-row__control-col">
          <input
            v-model="channelWelcomeTitle"
            type="text"
            class="ch-input"
            :placeholder="
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.PLACEHOLDER'
              )
            "
          />
        </div>
      </div>

      <!-- Welcome Tagline -->
      <div class="ch-form-row">
        <div class="ch-form-row__label-col">
          <label class="ch-form-label">{{
            $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.LABEL')
          }}</label>
        </div>
        <div class="ch-form-row__control-col">
          <Editor
            v-model="channelWelcomeTagline"
            :placeholder="
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.PLACEHOLDER'
              )
            "
            :max-length="255"
            :enabled-menu-options="welcomeTaglineEditorMenuOptions"
          />
        </div>
      </div>

      <!-- Greeting Toggle -->
      <div class="ch-form-row">
        <div class="ch-form-row__label-col">
          <label class="ch-form-label">{{
            $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.LABEL')
          }}</label>
          <p class="ch-form-hint">
            {{
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.HELP_TEXT'
              )
            }}
          </p>
        </div>
        <div class="ch-form-row__control-col">
          <select v-model="greetingEnabled" class="ch-select">
            <option :value="true">
              {{
                $t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.ENABLED'
                )
              }}
            </option>
            <option :value="false">
              {{
                $t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.DISABLED'
                )
              }}
            </option>
          </select>
        </div>
      </div>

      <!-- Greeting Message -->
      <div v-if="greetingEnabled" class="ch-form-row">
        <div class="ch-form-row__label-col">
          <label class="ch-form-label">{{
            $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.LABEL')
          }}</label>
        </div>
        <div class="ch-form-row__control-col">
          <GreetingsEditor
            v-model="greetingMessage"
            :placeholder="
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.PLACEHOLDER'
              )
            "
            :richtext="!textAreaChannels"
          />
        </div>
      </div>

      <!-- Submit -->
      <div class="ch-form-footer">
        <NextButton
          type="submit"
          :is-loading="uiFlags.isCreating"
          :disabled="!channelWebsiteUrl || !inboxName"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>

<style scoped>
.ch-form-page {
  padding: 28px 32px 80px;
  max-width: 860px;
  background: #ffffff;
  box-sizing: border-box;
}
.ch-form-page__intro {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
}
.ch-form-page__title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px;
}
.ch-form-page__desc {
  font-size: 13px;
  color: #4b5563;
  margin: 0;
}
.ch-form-row {
  display: flex;
  gap: 24px;
  padding: 16px 0;
  border-bottom: 1px solid #f3f4f6;
}
.ch-form-row:last-of-type {
  border-bottom: none;
}
.ch-form-row__label-col {
  width: 240px;
  flex-shrink: 0;
}
.ch-form-row__control-col {
  flex: 1;
  min-width: 0;
}
.ch-form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 2px;
}
.ch-form-hint {
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0;
  line-height: 1.5;
}
.ch-input {
  display: block;
  width: 100%;
  max-width: 400px;
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #111827;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.ch-input:focus {
  border-color: #4384f5;
  box-shadow: 0 0 0 3px rgba(67, 132, 245, 0.12);
}
.ch-select {
  display: block;
  width: 100%;
  max-width: 400px;
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #111827;
  outline: none;
  cursor: pointer;
}
.ch-select:focus {
  border-color: #4384f5;
  box-shadow: 0 0 0 3px rgba(67, 132, 245, 0.12);
}
.ch-form-footer {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}
</style>
